import React, { Component } from "react";
import Papa from "papaparse";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import { ApplicationState } from "../../../store";
import { bindActionCreators, Dispatch } from "redux";
import { hideUploadStudentDataModal, openUploadStudentDataModal } from "../../../store/onboarding/actions";
import { Spinner } from "react-bootstrap";
import { resolve } from "dns";
import _ from "lodash";
import { dibels_columns, vaildFileExtension } from "../../../utils/files";
import {
  CSV_FILE_TYPES,
  EXCEL_FILE_TYPES,
  FileType,
  detectFileType,
} from "./FileReaderHelpers";
import {
  LinkitHandler,
  MClassHandler,
  NJTSSHandler,
  ISteepHandler,
  DDMSHandler,
  GenericHandler,
} from "./handlers";

type DispatchProps = {
  onHide: () => void;
  openUploadStudentDataModal: () => void;
};

type OwnProps = {
  uploadCsvFile: (
    fileName: string,
    columnsNames: Array<string>,
    rawData: Array<any>,
    fileInputKey?: number
  ) => any;
  uploadMultipleCsvFile?: (uploadCSVArray:any, fileInputKey?: number) => any
};

type Props = OwnProps & DispatchProps;

type State = {
  isLinkIt: boolean;
  isDoubleHeaderLinkIt: boolean;
  fileName?: string;
  headers?: string[];
  originalData?: string;
  originalMultipleCSVData?: any;
  multiCSVheaders?: any;
  uploadCSVArray: any;
  workSheetLength: number;
  isAllProcessDone: boolean;
  showLoader: boolean;
  fileInputKey: number;
  detectedFileType?: FileType;
};

class DataFileReader extends Component<Props, State> {
  state: State = { 
    isLinkIt: false, 
    isDoubleHeaderLinkIt: false, 
    uploadCSVArray: {}, 
    originalMultipleCSVData: {}, 
    multiCSVheaders:{}, 
    workSheetLength:0, 
    showLoader: false, 
    isAllProcessDone: false, 
    fileInputKey: Date.now(),
    detectedFileType: undefined,
  };
  private fileInputRef = React.createRef<HTMLInputElement>();
  
  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files != null) {
      const file: File = event.target.files[0];
      if(!vaildFileExtension(file)) {
        if (this.fileInputRef.current) this.fileInputRef.current.value = "";
        this.forceUpdate();
        return;
      }
      const fileType = file
        ? file.type
          ? file.type
          : file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2)
        : "";

        this.handleFile(file, fileType);
    }
    // Always reset input value after handling
    if (this.fileInputRef.current) this.fileInputRef.current.value = "";
    this.setState({ fileInputKey: Date.now() })
  };

  /**
   * Main CSV data handler - routes to appropriate file type handler
   */
  handleCSVData = async (csvData: string, fileType: string) => {
    // Detect file type first
    const detectedType = detectFileType(csvData);
    await this.setState({ detectedFileType: detectedType, originalData: csvData });

    // Route to appropriate handler based on detected file type
    switch (detectedType) {
      case FileType.ISTEEP:
        this.handleISteepFile(csvData);
        break;
      
      case FileType.NJTSS:
        this.handleNJTSSFile(csvData);
        break;
      
      case FileType.MCLASS:
        this.handleMClassFile(csvData);
        break;
      
      case FileType.LINKIT: {
        await this.setState({ isLinkIt: true });
        // Preprocess Linkit data to merge double headers before parsing
        const preprocessedLinkitData = this.preprocessLinkitData(csvData);
        // Parse the preprocessed data with merged headers
        Papa.parse(preprocessedLinkitData, {
          complete: this.updateData,
          header: true,
          dynamicTyping: true,
          trimHeaders: true,
          skipEmptyLines: true,
          transform(value: string, field: string | number): any {
            return value.trim();
          },
          transformHeader(header: string): any {
            return header.trim();
          },
        });
        break;
      }
      
      case FileType.DDMS:
        this.handleDDMSFile(csvData);
        break;
      
      case FileType.GENERIC:
      default:
        this.handleGenericFile(csvData);
        break;
    }
  };

  /**
   * Preprocess Linkit CSV data - handles double headers and DIBELS columns
   * This must run BEFORE Papa.parse for Linkit files to merge double headers
   */
  preprocessLinkitData = (csvData: string): string => {
    const csvDataArray = csvData.split('\n');
    const csvRowStrArray = [];
    let isStringFound = false;
    
    const studentIDColumn = [
      'ID', 'Student ID', 'Student ID#', 'student_id',
      'StudentId', 'Student_ID', 'first_name', 'last_name',
      'First', 'Last'
    ];
    
    const doubleHeaderColumnHave = [
      dibels_columns.LNF,
      dibels_columns.PSF,
      dibels_columns["NWF-CLS"],
      dibels_columns["NWF-WRC"],
      dibels_columns.WRF,
      dibels_columns["ORF-WordsCorrect"],
      dibels_columns["ORF-Accuracy"],
      dibels_columns["Maze-Adjusted"],
      dibels_columns.Composite
    ];
    
    for (let i = 0; i < csvDataArray.length; i++) {
      let csvRowStr = csvDataArray[i];
      
      // Handle DIBELS title columns - fill empty cells with title
      if (csvRowStr.includes('"ID"')) {
        let csvRow = csvRowStr.split(',');
        const title = csvRow.find((row) => row.match('DIBELS'));
        csvRow = csvRow.map((rowData: any) => {
          if (rowData === "" && title !== undefined) {
            rowData = title;
          }
          return rowData;
        });
        csvRowStr = csvRow.join(',');
      }
      
      // Skip blank rows until header found
      if (!studentIDColumn.some((sid) => csvRowStr.includes(sid)) && !isStringFound) {
        continue;
      }
      
      // Handle double header for Linkit files
      if (csvDataArray[0] !== '' 
          && !csvDataArray[0].includes('StudentSummary')
          && !csvDataArray[0].includes('District_Name')
      ) {
        // Check if next row has DIBELS columns (double header indicator)
        if (csvDataArray[i + 1] 
            && doubleHeaderColumnHave.filter((double) => {
                const csvRow = csvDataArray[i + 1].toLowerCase().split(',');
                return double.some((col) => (csvRow || []).includes(col.toLowerCase()));
              }).length > 0
            && !this.state.isDoubleHeaderLinkIt
        ) {
          // Merge double headers
          const firstHeader = csvDataArray[i]
            .split(',')
            .filter((data) => data)  // Remove empty cells
            .map((str) => {
              // Remove numeric-only headers (years like "2023", "22", etc.)
              const fourDigitRegex = /\b\d{4}\b/g;
              const twoDigitRegex = /\b\d{2}\b/g;
              const oneDigitRegex = /\b\d{1}\b/g;
              if (fourDigitRegex.test(str) || twoDigitRegex.test(str) || oneDigitRegex.test(str)) {
                return "";  // Remove numeric headers
              } else {
                return str;
              }
            })
            .filter((data) => data)  // Remove empty again after filtering
            .join(',');
          
          // Clean second header
          csvDataArray[i + 1] = csvDataArray[i + 1].split(',').filter((data) => data).join(',');
          
          // Merge both headers
          csvRowStr = firstHeader.concat(',').concat(csvDataArray[i + 1]);
          
          // Remove the second header row from array
          csvDataArray.splice(i + 1, 1);
          
          // Set flag to prevent double processing
          this.setState({ isDoubleHeaderLinkIt: true });
        }
      }
      
      isStringFound = true;
      
      // Filter out blank rows
      const csvRowSubStringArray = csvRowStr.split(',');
      const isArrayBlank = csvRowSubStringArray.every((data) => data.trim() === '');
      
      if (!isArrayBlank) {
        csvRowStrArray.push(csvRowStr);
      }
    }
    
    return csvRowStrArray.join('\n');
  }

  /**
   * Handle iSteep file format
   */
  handleISteepFile = (csvData: string) => {
    const csvDataArray = csvData.split('\n');
    const csvRowStrArray = ISteepHandler.processISteepData(csvDataArray);
    const processedData = csvRowStrArray.join('\n');
    
    ISteepHandler.parseISteepData(processedData, this.updateData);
  };

  /**
   * Handle NJTSS file format
   */
  handleNJTSSFile = (csvData: string) => {
    const csvDataArray = csvData.split('\n');
    const csvRowStrArray = NJTSSHandler.processNJTSSData(csvDataArray);
    const processedData = csvRowStrArray.join('\n');
    
    NJTSSHandler.parseNJTSSData(processedData, this.updateData);
  };

  /**
   * Handle mClass file format
   */
  handleMClassFile = (csvData: string) => {
    const csvDataArray = csvData.split('\n');
    const csvRowStrArray = MClassHandler.processMClassData(
      csvDataArray,
      this.state.isDoubleHeaderLinkIt,
      (flag: boolean) => this.setState({ isDoubleHeaderLinkIt: flag })
    );
    const processedData = csvRowStrArray.join('\n');
    
    MClassHandler.parseMClassData(processedData, this.updateData);
  };

  /**
   * Handle DDMS file format
   */
  handleDDMSFile = (csvData: string) => {
    const csvDataArray = csvData.split('\n');
    const csvRowStrArray = DDMSHandler.processDDMSData(csvDataArray);
    const processedData = csvRowStrArray.join('\n');
    
    DDMSHandler.parseDDMSData(processedData, this.updateData);
  };

  /**
   * Handle generic CSV file format
   */
  handleGenericFile = (csvData: string) => {
    const csvDataArray = csvData.split('\n');
    const csvRowStrArray = GenericHandler.processGenericData(csvDataArray);
    const processedData = csvRowStrArray.join('\n');
    
    GenericHandler.parseGenericData(processedData, this.updateData);
  };

  /**
   * Handle multiple CSV data (for NJTSS files with Data Entry sheets)
   * Supports: Fall Data Entry, Winter Data Entry, Spring Data Entry
   */
  handleMultipleCSVData = (csvData: string, fileType: string, workSheetName?: string, sheetIndex?: number) => {
    const { processedData, period, year, grade } = NJTSSHandler.processNJTSSSheetData(csvData, workSheetName!);

    // Store period metadata
    const csvDataObj: any = {};
    csvDataObj[workSheetName!] = { period, year, grade };

    this.setState(
      (prevState: State) => ({
        originalMultipleCSVData: {
          ...prevState.originalMultipleCSVData,
          ...csvDataObj,
        },
      }),
      () => {
        NJTSSHandler.parseNJTSSMultiSheetData(
          processedData,
          this.updateMultipleData,
          workSheetName!,
          sheetIndex!
        );
      }
    );
  };

  getDataRange = (data: any) => {
    const dataWithValues = _.pickBy(data, (value, key) => !!value.v)
    const cellNamesWithValues = _.keys(dataWithValues)
    const cellsWithValues = cellNamesWithValues.map(cell => XLSX.utils.decode_cell(cell))
    const maxRow = _.max(cellsWithValues.map(cell => cell.r))
    const maxColumn = _.max(cellsWithValues.map(cell => cell.c))
    const lastCellName = XLSX.utils.encode_cell({ c: maxColumn!, r: maxRow! })
    return `A1:${lastCellName}`
  }

  handleFile =  (file: File, fileType: string) => {
    this.setState({ fileName: file.name });
    const reader = new FileReader();
    const readAsArrayBuffer = EXCEL_FILE_TYPES.includes(fileType);
    reader.onload = (e) => {
      if (reader.result) {
        if (CSV_FILE_TYPES.includes(fileType)) {
          this.handleCSVData(reader.result as string, fileType);
        } else if (EXCEL_FILE_TYPES.includes(fileType)) {
          const workbook = XLSX.read(reader.result, { type: "array" });
          
          // Check for NJTSS Data Entry sheets (Fall, Winter, Spring)
          if(NJTSSHandler.hasMultipleDataEntrySheets(workbook.SheetNames)) {
            const dataEntrySheets = NJTSSHandler.getDataEntrySheetNames(workbook.SheetNames);
            this.setState({ workSheetLength: dataEntrySheets.length });

            let sheetIndex = dataEntrySheets.length;
            for (let i = 0; i < dataEntrySheets.length; i++) {
              const sheetName = dataEntrySheets[i];
              const sheet = workbook.Sheets[sheetName];
              sheet['!ref'] = this.getDataRange(sheet);
              const asCSV = XLSX.utils.sheet_to_csv(sheet);
              this.handleMultipleCSVData(asCSV, "csv", sheetName, sheetIndex--); 
            }
          } else {
            // Single sheet file
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            sheet['!ref'] = this.getDataRange(sheet);
            const asCSV = XLSX.utils.sheet_to_csv(sheet);
            this.handleCSVData(asCSV, "csv");
          }
        } else {
          this.showError();
        }
      } else {
        this.showError();
      }
    };
    if (readAsArrayBuffer) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  showError = () => {
    toastr.error("Error", "This file format is not supported.");
  };

  /**
   * Update data after parsing - handles Linkit special cases
   */
  updateData = async (result: any, file: File) => {
    const columnsNames = result.meta.fields;
    const rawData = result.data;

    // Handle Linkit file format
    if (!this.state.isLinkIt) {
      const firstCellValueForLinkIt = "Selected tests";
      const linkitColumnMustHave = ["student", "id", '"id"'];
      const doubleHeaderColumnHave = ["lnf", "psf", "nwf","orf", "wrc", "cls", "wrf"];
      
      if (columnsNames[0] === firstCellValueForLinkIt) {
        if (rawData.length > 3) {
          if (
            rawData[0][firstCellValueForLinkIt] === "Period" &&
            rawData[1][firstCellValueForLinkIt] === "Filters"
          ) {
            await this.setState({ isLinkIt: true });

            const { originalData } = this.state;

            let count = 0;
            let i = 0;
            for (; i < originalData!.length; i++) {
              const char = originalData!.charAt(i);
              if (char === "\n") {
                count++;
              }

              if (count === 4) {
                break;
              }
            }

            const dataWithoutUpperSection = originalData!.substring(i);
            Papa.parse(dataWithoutUpperSection, {
              complete: this.updateData,
              header: true,
              dynamicTyping: true,
              trimHeaders: true,
              skipEmptyLines: true,
              transform(value: string, field: string | number): any {
                return value.trim();
              },
            });
            return;
          }
        }
      }

      const linkitConstants = ["Export Date", "Selected Tests"];
      const fifthCellValueForLinkIt = "Student";

      if (
        (rawData.length > 3 &&
          (rawData[3]?.[columnsNames[0]] === fifthCellValueForLinkIt ||
            rawData[4]?.[columnsNames[0]] === fifthCellValueForLinkIt)) ||
        (linkitConstants.indexOf(columnsNames[0]) != -1 &&
          linkitConstants.indexOf(rawData[0][columnsNames[0]]) != -1)
      ) {
        await this.setState({ isLinkIt: true });

        try {
          LinkitHandler.processLinkitData(
            this.state.originalData!,
            columnsNames,
            rawData,
            this.updateData
          );
          return;
        } catch (error: any) {
          toastr.error("Alert!!", error.message);
          this.props.onHide();
          this.props.openUploadStudentDataModal();
          document.body.click();
          return;
        }
      }
      
      this.props.uploadCsvFile(
        this.state.fileName!,
        columnsNames,
        rawData,
        this.state.fileInputKey
      );
    } else {
      this.props.uploadCsvFile(
        this.state.fileName!,
        columnsNames,
        rawData,
        this.state.fileInputKey
      );
    }
  };

  /**
   * Update multiple data (for NJTSS files with Data Entry sheets)
   */
  updateMultipleData = (result: any, file: File | undefined, workSheetName: string | undefined, sheetIndex: number | undefined) => {
    const columnsNames = result.meta.fields.filter((data: any) => data !== "");
    const rawData = result.data;

    if (rawData.length && rawData[0]!.Student === null) {
      rawData.splice(0, 1);
    }
    
    const csvHeaderObj: any = {};
    csvHeaderObj[workSheetName!] = columnsNames;
    
    this.setState(
      (prevState: State) => ({
        multiCSVheaders: {
          ...prevState.multiCSVheaders,
          ...csvHeaderObj,
        },
      }),
      () => {
        const { originalMultipleCSVData, multiCSVheaders } = this.state;
        const csvDataObj: any = {};
        csvDataObj[workSheetName!] = {
          period: originalMultipleCSVData[workSheetName!].period,
          grade: originalMultipleCSVData[workSheetName!].grade,
          year: originalMultipleCSVData[workSheetName!].year,
          fileName: this.state.fileName!,
          headers: multiCSVheaders[workSheetName!],
          rawData,
        };

        this.setState(
          (prevState: State) => ({
            uploadCSVArray: {
              ...prevState.uploadCSVArray,
              ...csvDataObj,
            },
          }),
          () => {
            if (sheetIndex === 1) {
              this.setState(
                {
                  isAllProcessDone: true,
                },
                () => {
                  this.setState({ showLoader: true });
                  if (this.state.isAllProcessDone) {
                    this.setState({ showLoader: false });
                    this.props.uploadMultipleCsvFile?.(this.state.uploadCSVArray, this.state.fileInputKey);
                  }
                }
              );
            }
          }
        );
      }
    );
  };

  render() {
    return (
      <>
      <input
        key={this.state.fileInputKey}
        ref={this.fileInputRef}
        style={{ display: "none" }}
        type="file"
        accept={".csv, text/csv, .xlsx"}
        onChange={this.handleChange}
      />
      {this.state.showLoader ? <Spinner animation="border" size="sm" className="ml-2" /> : ''}
      </>
    );
  }
}



const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      onHide: hideUploadStudentDataModal,
      openUploadStudentDataModal: openUploadStudentDataModal,
    },
    dispatch
  );
export default connect(null, mapDispatchToProps)(DataFileReader);

